import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { htmlToExcerpt } from "../../lib/utils";
import PageHero from "../../components/public/PageHero";
import { Search, ExternalLink } from "lucide-react";

export default function BuscadorSocios({ tier, title, subtitle }) {
  const { t, pick, lang } = useLang();
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(tier || "all");

  useEffect(() => {
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return members.filter((m) => {
      if (tier && m.tier !== tier) return false;
      if (!tier && filter !== "all" && m.tier !== filter) return false;
      if (q) {
        const ql = q.toLowerCase();
        return m.name.toLowerCase().includes(ql) || (m.sector || "").toLowerCase().includes(ql);
      }
      return true;
    });
  }, [members, q, filter, tier]);

  return (
    <div data-testid="members-directory">
      <PageHero title={title || t("members.title")} subtitle={subtitle || t("members.subtitle")} />
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="bg-white border border-gray-100 p-6 mb-10 flex flex-col md:flex-row gap-4 items-stretch md:items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("members.search")}
              className="w-full pl-11 pr-4 py-3.5 border border-gray-200 focus:border-[#0F2040] outline-none"
              data-testid="member-search"
            />
          </div>
          {!tier && (
            <div className="flex gap-2" data-testid="member-tier-filter">
              {["all", "premium", "standard", "basico"].map((tk) => (
                <button
                  key={tk}
                  onClick={() => setFilter(tk)}
                  className={`px-4 py-3 text-sm font-medium border transition-colors ${
                    filter === tk
                      ? "bg-[#0F2040] text-white border-[#0F2040]"
                      : "border-gray-200 text-gray-700 hover:border-[#0F2040]"
                  }`}
                  data-testid={`tier-filter-${tk}`}
                >
                  {tk === "all" ? t("members.all") : t(`members.${tk}`)}
                </button>
              ))}
            </div>
          )}
        </div>

        {filtered.length === 0 ? (
          <p className="text-center text-gray-500 py-20">{t("members.noResults")}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {filtered.map((m) => (
              <div key={m.id} className="bg-white p-7 hover:bg-[#FAFAFA] transition-colors group" data-testid={`member-card-${m.slug}`}>
                <div className="flex justify-between items-start mb-3">
                  <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 ${
                    m.tier === "premium" ? "bg-[#0F2040] text-cecit-gold" :
                    m.tier === "standard" ? "bg-gray-100 text-[#0F2040]" :
                    "bg-gray-50 text-gray-600"
                  }`}>
                    {t(`members.${m.tier}`)}
                  </span>
                </div>
                {m.logo_url && (
                  <img src={m.logo_url} alt={m.name} className="h-12 w-auto object-contain mb-4 grayscale group-hover:grayscale-0 transition-all" />
                )}
                <h3 className="font-serif text-2xl text-[#0F2040] mb-1.5">{m.name}</h3>
                {m.sector && <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">{m.sector}</p>}
                <p className="text-sm text-gray-600 line-clamp-3 mb-4">{htmlToExcerpt(pick(m, "description"), 200)}</p>
                {m.website && m.website !== "#" && (
                  <a href={m.website} target="_blank" rel="noreferrer" className="text-sm font-medium text-[#C8102E] hover:text-[#0F2040] inline-flex items-center gap-1.5">
                    {t("members.visit")} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
